import { useCallback, useEffect, useRef } from "react";
import { useQuickLook } from "../componets/common/QuickLook/quickLookContext";
import { VrReportContentMode } from "./vrReportContentMode";

export type SubmittedVrReportResult = "opened";

const revokeObjectUrl = (url: string) => {
  if (typeof window.URL.revokeObjectURL === "function") {
    window.URL.revokeObjectURL(url);
  }
};

// Opens the submitted VR report in QuickLook. No snapshot preflight — the
// button's status/permission gating decides availability, and the server
// resolves snapshot-or-fallback (or errors), so a failed view just throws.
export const useSubmittedVrReportQuickLook = (dataProvider: any) => {
  const quickLook = useQuickLook();
  const activeObjectUrlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (activeObjectUrlRef.current) {
        revokeObjectUrl(activeObjectUrlRef.current);
      }
    },
    []
  );

  return useCallback(
    async (
      trnId: string | number,
      contentMode?: VrReportContentMode
    ): Promise<SubmittedVrReportResult> => {
      const report = await dataProvider.viewVrReportSnapshot({
        trnId,
        openInNewWindow: false,
        content: contentMode,
      });
      const reportUrl = report?.data?.url;
      if (!reportUrl) throw new Error("Failed to open the submitted report");

      const previousUrl = activeObjectUrlRef.current;
      activeObjectUrlRef.current = reportUrl;
      quickLook.openEntry(`submitted-vr-report-${trnId}`, {
        fileUrl: reportUrl,
        fileName: "Verification Report.pdf",
        mimeType: "application/pdf",
        sectionKey: "submitted-vr-report",
        sourceId: trnId,
      });

      // Let QuickLook render the replacement before releasing the prior blob.
      if (previousUrl && previousUrl !== reportUrl) {
        window.setTimeout(() => revokeObjectUrl(previousUrl), 0);
      }

      return "opened";
    },
    [dataProvider, quickLook.openEntry]
  );
};
